import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

import useThirdPartyInvoice from "../../hooks/useThirdPartyInvoice";
import useData from "../../hooks/useData";
import useLocalization from "../../hooks/useLocalization";

import Input from "../elements/Input";
import Select from "../elements/Select";
import DateField from "../elements/Date";
import Switch from "../elements/Switch";
import Dialog_YesNo from "../ui/Dialog_YesNo";

const ThirdPartyInvoiceEditor = ({ clientId, projectId, onClose }) => {
  const { getFromServer } = useData();
  const { getLabel } = useLocalization();
  const {
    currentInvoice,
    getInvoiceByClientProject,
    createInvoice,
    updateInvoice,
    addLine,
    updateLine,
    deleteLine,
  } = useThirdPartyInvoice();

  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoiceData, setInvoiceData] = useState(null);
  const [editingLineId, setEditingLineId] = useState(null);
  const [lineFormData, setLineFormData] = useState({
    vendorInvoiceCategoryId: "",
    description: "",
    amount: "",
    taxExempt: false,
  });
  const [openLineDialog, setOpenLineDialog] = useState(false);
  const [openAddCategoryDialog, setOpenAddCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryTaxExempt, setNewCategoryTaxExempt] = useState(false);
  const [deleteConfirmLineId, setDeleteConfirmLineId] = useState(null);

  useEffect(() => {
    loadData();
  }, [clientId, projectId]);

  const loadData = async () => {
    // Load vendors
    const vendorData = await getFromServer({
      entityName: "Vendor",
      filter: { isActive: true },
    });
    setVendors(vendorData || []);

    // Load categories
    const categoryData = await getFromServer({
      entityName: "VendorInvoiceCategory",
      filter: { isActive: true },
    });
    setCategories(categoryData || []);

    // Load clients for new invoice creation
    const clientData = await getFromServer({
      entityName: "Client",
      filter: { isActive: true },
    });
    setClients(clientData || []);

    // Load projects for new invoice creation
    const projectData = await getFromServer({
      entityName: "Project",
      filter: { isActive: true },
    });
    setProjects(projectData || []);

    // Load existing invoice if it exists
    if (clientId && projectId) {
      await getInvoiceByClientProject(clientId, projectId);
    }
  };

  useEffect(() => {
    if (currentInvoice) {
      setInvoiceData({
        ...currentInvoice,
        lines: currentInvoice.lines || [],
      });
    } else {
      // Initialize empty invoice for creation
      setInvoiceData({
        vendorId: "",
        invoiceDate: new Date(),
        vendorInvoiceNumber: "",
        lines: [],
        notes: "",
      });
    }
  }, [currentInvoice]);

  const handleInvoiceFieldChange = (field, value) => {
    if (!invoiceData) return;
    setInvoiceData({
      ...invoiceData,
      [field]: value,
    });
  };

  const handleSaveInvoice = async () => {
    if (!invoiceData) return;

    // Validate required fields
    if (!clientId || !projectId) {
      console.error("ClientId or ProjectId missing:", { clientId, projectId });
      alert("Client and Project are required");
      return;
    }

    const dataToSave = {
      clientId,
      projectId,
      vendorId: invoiceData.vendorId,
      invoiceDate: invoiceData.invoiceDate,
      vendorInvoiceNumber: invoiceData.vendorInvoiceNumber || "",
      lines: invoiceData.lines || [],
      notes: invoiceData.notes || "",
    };

    if (invoiceData.id) {
      await updateInvoice(invoiceData.id, dataToSave);
    } else {
      await createInvoice(dataToSave);
    }
  };

  const handleOpenLineForm = (line = null) => {
    if (line) {
      setEditingLineId(line._id);
      setLineFormData({
        vendorInvoiceCategoryId: line.vendorInvoiceCategoryId,
        description: line.description,
        amount: line.amount.toString(),
        taxExempt: line.taxExempt,
      });
    } else {
      setEditingLineId(null);
      setLineFormData({
        vendorInvoiceCategoryId: "",
        description: "",
        amount: "",
        taxExempt: false,
      });
    }
    setOpenLineDialog(true);
  };

  const handleSaveLine = async () => {
    if (
      !invoiceData ||
      !lineFormData.vendorInvoiceCategoryId ||
      !lineFormData.description ||
      !lineFormData.amount
    ) {
      alert("All line fields required");
      return;
    }

    const lineData = {
      vendorInvoiceCategoryId: lineFormData.vendorInvoiceCategoryId,
      description: lineFormData.description,
      amount: parseFloat(lineFormData.amount),
      taxExempt: lineFormData.taxExempt,
    };

    let updatedInvoice;
    if (editingLineId) {
      updatedInvoice = await updateLine(
        invoiceData.id,
        editingLineId,
        lineData,
      );
    } else {
      updatedInvoice = await addLine(invoiceData.id, lineData);
    }

    if (updatedInvoice) {
      setInvoiceData(updatedInvoice);
      setOpenLineDialog(false);
    }
  };

  const handleDeleteLine = async () => {
    if (!deleteConfirmLineId || !invoiceData) return;

    const updatedInvoice = await deleteLine(
      invoiceData.id,
      deleteConfirmLineId,
    );
    if (updatedInvoice) {
      setInvoiceData(updatedInvoice);
      setDeleteConfirmLineId(null);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName) return;

    // This would call a backend action to add a new category
    // For now, just refreshing the category list
    // TODO: Implement category creation endpoint
    setOpenAddCategoryDialog(false);
    setNewCategoryName("");
    setNewCategoryTaxExempt(false);
    await loadData();
  };

  if (!invoiceData || !invoiceData.id) {
    // Show create form
    if (!invoiceData) {
      return (
        <Box padding={2}>
          <Typography>Loading...</Typography>
        </Box>
      );
    }

    // When creating new invoice, require client and project
    if (!clientId || !projectId) {
      return (
        <Box padding={2}>
          <Typography
            color="error"
            variant="h6"
          >
            Error: Client and Project Required
          </Typography>
          <Typography
            variant="body2"
            sx={{ marginTop: 1 }}
          >
            Please select a client and project from the list to create a
            third-party invoice.
          </Typography>
        </Box>
      );
    }

    return (
      <Box padding={2}>
        <Typography variant="h6">Create Third-Party Invoice</Typography>
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          marginTop={2}
        >
          <Select
            label="Client"
            items={clients}
            value={clientId || ""}
            onChange={(value) => {
              // This would require passing setClientId from parent
              // For now, show error that client must be selected from list
            }}
            required
            disabled
          />
          <Select
            label="Project"
            items={projects}
            value={projectId || ""}
            onChange={(value) => {
              // This would require passing setProjectId from parent
              // For now, show error that project must be selected from list
            }}
            required
            disabled
          />
          <Select
            label="Vendor"
            items={vendors}
            value={invoiceData?.vendorId || ""}
            onChange={(value) => handleInvoiceFieldChange("vendorId", value)}
            required
          />
          <DateField
            label="Invoice Date"
            value={invoiceData?.invoiceDate || new Date()}
            onChange={(value) => handleInvoiceFieldChange("invoiceDate", value)}
          />
          <Button
            variant="contained"
            onClick={handleSaveInvoice}
          >
            Create Invoice
          </Button>
        </Box>
      </Box>
    );
  }

  const selectedVendor = vendors.find((v) => v.id === invoiceData.vendorId);

  return (
    <Box padding={2}>
      <Typography variant="h6">Third-Party Invoice</Typography>

      {/* Header fields */}
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        marginTop={2}
        marginBottom={2}
      >
        <Input
          label="Vendor"
          value={selectedVendor?.name || ""}
          disabled
          fullwidth
        />
        <Input
          label="Vendor Invoice #"
          value={invoiceData.vendorInvoiceNumber}
          onChange={(value) =>
            handleInvoiceFieldChange("vendorInvoiceNumber", value)
          }
          fullwidth
        />
        <DateField
          label="Invoice Date"
          value={invoiceData.invoiceDate}
          onChange={(value) => handleInvoiceFieldChange("invoiceDate", value)}
        />
        <Input
          label="Notes"
          value={invoiceData.notes}
          onChange={(value) => handleInvoiceFieldChange("notes", value)}
          multiline
          rows={3}
          fullwidth
        />
      </Box>

      {/* Lines table */}
      <Box marginTop={2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={1}
        >
          <Typography variant="subtitle2">Line Items</Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => handleOpenLineForm()}
          >
            Add Line
          </Button>
        </Box>

        <Table
          size="small"
          sx={{ border: "1px solid #ddd" }}
        >
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="center">Tax Exempt</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoiceData.lines && invoiceData.lines.length > 0 ? (
              invoiceData.lines.map((line) => {
                const category = categories.find(
                  (c) => c.id === line.vendorInvoiceCategoryId,
                );
                return (
                  <TableRow key={line._id}>
                    <TableCell>{category?.name || "Unknown"}</TableCell>
                    <TableCell>{line.description}</TableCell>
                    <TableCell align="right">
                      ${line.amount.toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      {line.taxExempt ? "✓" : ""}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenLineForm(line)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteConfirmLineId(line._id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                >
                  No line items
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Totals */}
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={3}
          marginTop={2}
          paddingRight={2}
        >
          <Box>
            <Typography variant="body2">Subtotal:</Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
            >
              ${(invoiceData.subtotal || 0).toFixed(2)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">Tax (13%):</Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
            >
              ${(invoiceData.taxAmount || 0).toFixed(2)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">Total:</Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
            >
              ${(invoiceData.total || 0).toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Action buttons */}
      <Box
        display="flex"
        gap={1}
        marginTop={3}
      >
        <Button
          variant="contained"
          onClick={handleSaveInvoice}
        >
          Save Invoice
        </Button>
        <Button
          variant="outlined"
          onClick={onClose}
        >
          Close
        </Button>
      </Box>

      {/* Line item dialog */}
      <Dialog
        open={openLineDialog}
        onClose={() => setOpenLineDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingLineId ? "Edit Line Item" : "Add Line Item"}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: 2,
          }}
        >
          <Select
            label="Category"
            items={categories}
            value={lineFormData.vendorInvoiceCategoryId}
            onChange={(value) =>
              setLineFormData({
                ...lineFormData,
                vendorInvoiceCategoryId: value,
              })
            }
            required
          />
          <Button
            variant="text"
            size="small"
            onClick={() => setOpenAddCategoryDialog(true)}
          >
            + Add New Category
          </Button>
          <Input
            label="Description"
            value={lineFormData.description}
            onChange={(value) =>
              setLineFormData({ ...lineFormData, description: value })
            }
            fullwidth
            required
          />
          <Input
            label="Amount"
            value={lineFormData.amount}
            onChange={(value) =>
              setLineFormData({ ...lineFormData, amount: value })
            }
            type="number"
            fullwidth
            required
          />
          <Switch
            label="Tax Exempt"
            value={lineFormData.taxExempt}
            onChange={(value) =>
              setLineFormData({ ...lineFormData, taxExempt: value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLineDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveLine}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add category dialog */}
      <Dialog
        open={openAddCategoryDialog}
        onClose={() => setOpenAddCategoryDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: 2,
          }}
        >
          <Input
            label="Category Name"
            value={newCategoryName}
            onChange={(value) => setNewCategoryName(value)}
            fullwidth
            required
          />
          <Switch
            label="Tax Exempt"
            value={newCategoryTaxExempt}
            onChange={(value) => setNewCategoryTaxExempt(value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddCategoryDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddCategory}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog_YesNo
        open={Boolean(deleteConfirmLineId)}
        title="Delete Line Item?"
        message="Are you sure you want to delete this line item?"
        onYes={handleDeleteLine}
        onNo={() => setDeleteConfirmLineId(null)}
      />
    </Box>
  );
};

export default ThirdPartyInvoiceEditor;
